import React from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import ChartCard from './ChartCard';
import {
    institutionLabel,
    INSTITUTION_KEYS,
} from '@/shared/constants/institutions';
import { money, pounds, whole } from './format';
import { AXIS_TEXT, GRID, NAVY, TEAL } from './palette';
import { svgLegend } from './svgLegend';

const HATCH = 'afe-hatch-received';
const UNKNOWN = 'unknown';

// Given is a solid navy bar and received a teal bar with navy diagonal
// hatching and a navy outline, so the two read apart in grayscale and for
// color-blind viewers, and teal never has to stand alone on white.
const LEGEND = svgLegend([
    {
        label: 'Given',
        swatch: <rect width="14" height="14" fill={NAVY} />,
    },
    {
        label: 'Received',
        swatch: (
            <rect
                width="14"
                height="14"
                fill={`url(#${HATCH})`}
                stroke={NAVY}
                strokeWidth="1.5"
            />
        ),
    },
]);

const name = (key) => (key === UNKNOWN ? 'Unknown' : institutionLabel(key));

/**
 * Pounds each institution gave and received. "Unknown" is the receiving
 * side of a transfer an owner completed without an accepted request, so it
 * has a received bar and nothing given.
 */
function InstitutionChart({ giving, receiving }) {
    const keys = [...INSTITUTION_KEYS, UNKNOWN];
    const figures = (side, key) =>
        side[key] || { transfers: 0, pounds: 0, dollars: 0 };
    const data = keys.map((key) => ({
        name: name(key),
        given: figures(giving, key).pounds,
        received: figures(receiving, key).pounds,
    }));

    return (
        <ChartCard
            title="Pounds by institution"
            filename="furniture-exchange-pounds-by-institution.png"
            summary={
                'Pounds diverted that each institution gave and received. ' +
                'Unknown is a transfer completed with no accepted request.'
            }
            table={{
                corner: 'Institution',
                columns: [
                    'Given (transfers)',
                    'Given (pounds)',
                    'Given (dollars)',
                    'Received (transfers)',
                    'Received (pounds)',
                    'Received (dollars)',
                ],
                rows: keys.map((key) => {
                    const gave = figures(giving, key);
                    const got = figures(receiving, key);
                    return {
                        key,
                        label: name(key),
                        values: [
                            whole(gave.transfers),
                            pounds(gave.pounds),
                            money(gave.dollars),
                            whole(got.transfers),
                            pounds(got.pounds),
                            money(got.dollars),
                        ],
                    };
                }),
            }}
        >
            <ResponsiveContainer width="100%" height={300}>
                <BarChart
                    data={data}
                    margin={{ top: 34, right: 4, left: 0, bottom: 4 }}
                    accessibilityLayer={false}
                >
                    <defs>
                        <pattern
                            id={HATCH}
                            width="8"
                            height="8"
                            patternUnits="userSpaceOnUse"
                        >
                            <rect width="8" height="8" fill={TEAL} />
                            <path
                                d="M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2"
                                stroke={NAVY}
                                strokeWidth="1.5"
                            />
                        </pattern>
                    </defs>
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis dataKey="name" tick={AXIS_TEXT} interval={0} />
                    <YAxis width={48} tick={AXIS_TEXT} tickFormatter={whole} />
                    <Tooltip formatter={(value) => pounds(value)} />
                    <Bar
                        dataKey="given"
                        name="Given"
                        fill={NAVY}
                        isAnimationActive={false}
                    >
                        <LabelList
                            dataKey="given"
                            position="top"
                            formatter={whole}
                            fill={NAVY}
                            fontSize={11}
                        />
                    </Bar>
                    <Bar
                        dataKey="received"
                        name="Received"
                        fill={`url(#${HATCH})`}
                        stroke={NAVY}
                        strokeWidth={1.5}
                        isAnimationActive={false}
                    >
                        <LabelList
                            dataKey="received"
                            position="top"
                            formatter={whole}
                            fill={NAVY}
                            fontSize={11}
                        />
                    </Bar>
                    {LEGEND}
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}

export default InstitutionChart;
